import * as LDClient from 'launchdarkly-js-client-sdk'
import Vue from 'vue'
import {
  ApiService,
} from './api'
import {
  CDebug,
  CError,
  CLog,
  CWarn,
} from './consoleMixin'
import { isProduction } from './helpers'

export const launchDarklyObservables = Vue.observable({})

export class LaunchDarkly {
  static #client = null;
  static #originalContext = null;
  static #originalFlags = {};

  constructor () {
    throw new Error('Could not instantiate Launch Darkly')
  }

  static #buildContext (user) {
    const defaultAttributes = {
      email: user.email,
      name: user.email,
    }

    if (user.employee) {
      return {
        key: user.employee.id,
        kind: 'employee',
        ...defaultAttributes,
      }
    }
    else if (user.client) {
      return {
        key: user.client.id,
        kind: 'client',
        ...defaultAttributes,
      }
    }
    else {
      return {
        key: user.email,
        kind: 'user',
        ...defaultAttributes,
      }
    }
  }

  static #buildOptions () {
    if (isProduction()) {
      return {
        logger: {
          debug: (message) => {
            return CDebug(message)
          },
          info: (message) => {
            return CLog(message)
          },
          warn: (message) => {
            return CWarn(message)
          },
          error: (message) => {
            return CError(message)
          },
        },
      }
    }
  }

  static #isInitialized () {
    return LaunchDarkly.#client != null && LaunchDarkly.#originalContext != null
  }

  static async close () {
    try {
      if (LaunchDarkly.#isInitialized()) {
        CLog('Closing LaunchDarkly for context', LaunchDarkly.#client.getContext())
        await LaunchDarkly.#client.close()
        LaunchDarkly.#client = null
        LaunchDarkly.#originalContext = null
      }
    }
    catch (error) {
      CError('Could not shut down LaunchDarkly client', error)
    }
  }

  static context () {
    return LaunchDarkly.#client == null
      ? null
      : LaunchDarkly.#client.getContext()
  }

  static async init (user) {
    if (LaunchDarkly.#isInitialized()) {
      CWarn('Launch Darkly client is already initialized')
      return
    }
    if (process.env.VUE_APP_ENV === 'test') {
      CWarn('Launch Darkly client initilization skipped')
      return
    }

    const key = 'LAUNCH_DARKLY'
    const config = await ApiService.getConfig({
      keys: key,
    })
    const keyValue = config.data[key]
    if (!keyValue) {
      CError('LaunchDarkly key not found')
      return
    }

    const context = LaunchDarkly.#buildContext(user)
    CLog('Initializing LaunchDarkly for user', user.email, 'with context', context)

    try {
      LaunchDarkly.#client = LDClient.initialize(keyValue, context, LaunchDarkly.#buildOptions())
      await LaunchDarkly.#client.waitForInitialization()
      LaunchDarkly.#originalContext = context
      LaunchDarkly.#originalFlags = LaunchDarkly.#client.allFlags()

      CLog('LaunchDarkly initialized for user', user.email, 'with context', LaunchDarkly.#client.getContext())

      // Update observables
      for (const [ key, value ] of Object.entries(LaunchDarkly.#originalFlags)) {
        Vue.set(launchDarklyObservables, key, value)
      }

      LaunchDarkly.#client.on('change', (settings) => {
        // https://bobtail.atlassian.net/browse/SM-1709
        // Do not respond to context changes for other users (such as a client being viewed by an employee)
        if (LaunchDarkly.#originalContext.key !== LaunchDarkly.#client.getContext().key) {
          return
        }

        // Only the flags changed will be returned
        CLog(`LaunchDarkly context`, LaunchDarkly.#client.getContext() ,'has flag values', settings)


        // https://bobtail.atlassian.net/browse/SM-1709
        for (const [ key, value ] of Object.entries(settings)) {
          Vue.set(launchDarklyObservables, key, value.current)
        }
      })
    }
    catch (error) {
      CError('Could not initialize LaunchDarkly', error)
    }
  }

  static isClientContext () {
    if (LaunchDarkly.#isInitialized()) {
      return false
    }

    return LaunchDarkly.#client.getContext().kind === 'client'
  }

  static isFlagEnabled (featureFlag, checkAgainstOriginalContext = true) {
    if (!LaunchDarkly.#isInitialized()) {
      CLog(`Checking if feature flag is enabled, but LaunchDarkly is not initialized`)
      return false
    }
    if (checkAgainstOriginalContext) {
      const { key: currentKey, kind: currentKind } = LaunchDarkly.#client.getContext()
      const { key: originalKey, kind: originalKind } = LaunchDarkly.#originalContext
      if (currentKey != originalKey || currentKind != originalKind) {
        return false
      }
    }
    // .variation triggers the onChange event initialized in LaunchDarkly.init(), even for users not initialized on load
    return LaunchDarkly.#client.variation(featureFlag, false)
  }

  static isFlagEnabledForClient (clientId, featureFlag) {
    if (LaunchDarkly.#client == null) {
      return Promise.resolve(false)
    }

    return new Promise((resolve, reject) => {
      LaunchDarkly.#client
        .identify({
          key: clientId,
          kind: 'client',
        })
        .then(() => {
          const result = LaunchDarkly.isFlagEnabled(featureFlag, false)

          LaunchDarkly.#client
            .identify(LaunchDarkly.#originalContext)
            .then(() => {
              return resolve(result)
            })
            .catch((error) => {
              CError('Could not switch LaunchDarkly context back from client context', clientId, error)
              resolve(false)
            })
        })
        .catch((error) => {
          CError('Could not switch LaunchDarkly context for client', clientId, error)
          return reject(false)
        })
    })
  }
}
